/* eslint-disable class-methods-use-this */
/* eslint-disable guard-for-in */
import React from 'react';
import {
  get, find, sortBy, isEmpty, concat,
} from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import MUIDataTable from 'mui-datatables';
import {
  Paper, Grid, Typography,
} from '@mui/material';
import { Stop } from '@mui/icons-material';
import Spinner from '../../common/spinner';
import CustomToolbar from '../custom-toolbar';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import WarnBeforeDeleting from '../../common/warn-before-deleting';
import {
  shouldFetchList,
  RunScheduleStatusNames,
  localeTimeFormat,
  timeZoneHint,
  FeatureFlagTypes,
  runTypes,
  daysObject,
  localeFullYearDateFormat,
  DataDriveRunTypes,
} from '../../../lib/utils';

import marketActions from '../../../actions/markets';
import runScheduleActions from '../../../actions/runSchedules';
import wranglerActions from '../../../actions/wranglers';
import zoneActions from '../../../actions/zones';
import * as snackbarActions from '../../../actions/snackbar';
import vehicleActions from '../../../actions/vehicles';
import locationActions from '../../../actions/locations';
import featureFlagsActions from '../../../actions/featureFlags';
import RunActionDropdown from './run-action-dropdown';
import { RunScheduleModal } from './run-schedule-modal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  root: {
    width: '95%',
    margin: '0 auto',
    overflowX: 'auto',
    borderTopRadius: 0,
  },
};

const NAME = 'Name';
const ASSIGNEE = 'Assignee';
const SCHEDULE_TIME = 'Scheduled Time';
const ROUTE = 'Route';
const TYPE = 'Type';
const VAN = 'Vans';
const STATUS = 'Status';
const EDIT_RUN_SCHEDULE = 'Edit Run Schedule';
const DELETE_RUN_SCHEDULE = 'Delete Run Schedule';
const DELETE_WARN = 'Are you sure you want to delete this Run Schedule?';
const DELETE_CONFIRM = 'Successfully deleted Run Schedule';
const DELETE_ERROR = 'Error deleting Run Schedule';

class BaseRunScheduleTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isRunScheduleModalOpen: false,
      editing: false,
      editRunSchedule: {},
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    };

    this.handleAddRunSchedule = this.handleAddRunSchedule.bind(this);
    this.handleEditRunSchedule = this.handleEditRunSchedule.bind(this);
    this.handleCloseRunScheduleModal = this.handleCloseRunScheduleModal.bind(this);
    this.handleSaveRunSchedule = this.handleSaveRunSchedule.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.getLocationsForZone = this.getLocationsForZone.bind(this);
    this.getVehiclesForZone = this.getVehiclesForZone.bind(this);
    this.getZonesForRunSchedule = this.getZonesForRunSchedule.bind(this);
    this.getVehiclesAndLocations = this.getVehiclesAndLocations.bind(this);
    this.getAssignedOAs = this.getAssignedOAs.bind(this);
    this.renderAssignedOAs = this.renderAssignedOAs.bind(this);
  }

  componentDidMount() {
    const {
      match,
      runSchedules,
      featureFlags,
      zones,
      wranglers,
      marketVehicles,
      marketLocations,
      fetchRunSchedules,
      fetchFeatureFlags,
      fetchZones,
      fetchWranglers,
      fetchVehicles,
      fetchLocations,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    if (marketId) {
      if (shouldFetchList(marketId, runSchedules)) {
        fetchRunSchedules({ market_id: marketId }).catch((error) => {
          showErrorSnackbar(`Error fetching run schedules: ${error}`);
        });
      }
      if (shouldFetchList(marketId, featureFlags)) fetchFeatureFlags(marketId);
      if (shouldFetchList(marketId, zones)) fetchZones({ market_id: marketId });
      if (shouldFetchList(marketId, wranglers)) fetchWranglers({ market_id: marketId });
      if (shouldFetchList(marketId, marketVehicles)) fetchVehicles({ market_id: marketId });
      if (shouldFetchList(marketId, marketLocations)) fetchLocations({ market_id: marketId });
    }
  }

  onDeleteRunSchedule(runSchedule) {
    this.setState({ editRunSchedule: runSchedule, warnBeforeDeleting: true });
  }

  get runSchedules() {
    const runSchedules = get(this, 'props.runSchedules', []);
    return sortBy(runSchedules, (runSchedule) => runSchedule.name.toLowerCase());
  }

  get tableOptions() {
    const { runSchedules, tableOptions } = this.props;
    const basicOptions = tableOptions;
    basicOptions.onRowClick = (rowData) => {
      const matchingRunSchedule = find(
        runSchedules,
        (runSchedule) => runSchedule._id === rowData[0],
      );
      this.handleEditRunSchedule(matchingRunSchedule);
    };

    basicOptions.customToolbar = () => (
      <CustomToolbar
        onClick={this.handleAddRunSchedule}
        type="Add"
      />
    );
    return basicOptions;
  }

  get columns() {
    const {
      addToColumnData, selectedFilters, primarySort, selectedMarket,
    } = this.props;
    const tz = get(selectedMarket, 'tz', 'America/New_York');

    const columnData = [
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: NAME,
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: SCHEDULE_TIME,
        options: {
          sort: false,
          filter: false,
          hint: timeZoneHint(tz),
          customBodyRender: (obj) => {
            const time = get(obj, 'time', '');
            const days = get(obj, 'days', '');
            const duration = get(obj, 'duration', 1);
            const doesRepeat = get(obj, 'doesRepeat');
            const daysRendered = days.map((day) => daysObject[day]);
            if (time) {
              return (
                <Grid>
                  <Typography>
                    {`${dayjs(time).tz(tz).format(localeTimeFormat)} - ${dayjs(time)
                      .tz(tz)
                      .add(duration, 'hours')
                      .format(localeTimeFormat)}`}
                  </Typography>
                  <Typography
                    style={{ paddingTop: 0, fontSize: 12 }}
                    noWrap
                  >
                    {doesRepeat
                      ? daysRendered.join(', ')
                      : `${dayjs(time).tz(tz).format(localeFullYearDateFormat)}`}
                  </Typography>
                </Grid>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: ASSIGNEE,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (assignees) => {
            if (assignees) {
              return (
                <Grid>
                  {assignees.map((assignee, i) => (
                    this.renderAssignedOAs(assignees, assignee, i)
                  ))}
                </Grid>
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${ASSIGNEE}: ${v}`,
          },
        },
      },
      {
        name: ROUTE,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => {
            if (data) {
              if (data.length === 0) {
                return (
                  <Grid>
                    <div style={{ display: 'flex' }}>
                      <Stop style={{ color: '#000000' }} />
                      <Typography
                        style={{ marginTop: 2 }}
                        noWrap
                      >
                        DataDrive
                      </Typography>
                    </div>
                  </Grid>
                );
              } else {
                return (
                  <Grid>
                    {data.map((r) => (
                      <div
                        style={{ display: 'flex' }}
                        key={r._id}
                      >
                        <Stop style={{ color: r.hexColor }} />
                        <Typography
                          style={{ marginTop: 2 }}
                          noWrap
                        >
                          {r.name}
                        </Typography>
                      </div>
                    ))}
                  </Grid>
                );
              }
            } else {
              return '';
            }
          },
        },
      },
      {
        name: TYPE,
        options: {
          sort: false,
          customBodyRender: (value) => {
            if (value) {
              return <Typography>{value}</Typography>;
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${TYPE}: ${v}`,
          },
        },
      },
      {
        name: VAN,
        options: {
          sort: false,
          customBodyRender: (value) => {
            if (value) {
              return <Typography>{value}</Typography>;
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${VAN}: ${v}`,
          },
        },
      },
      {
        name: STATUS,
        options: {
          sort: false,
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: '',
        options: {
          filter: false,
          sort: false,
          viewColumns: false,
          customBodyRender: (runSchedule) => {
            if (runSchedule) {
              return (
                <RunActionDropdown
                  isRun={false}
                  matchingRun={runSchedule}
                  isDynamic={get(runSchedule, 'isDynamic', false)}
                  onChange={this.handleDropdownChange}
                />
              );
            } else {
              return '';
            }
          },
        },
      },
    ];

    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const { runSchedules = [], equityDeployMarkets, selectedMarket } = this.props;
    const data = runSchedules.map((runSchedule) => {
      const runScheduleWranglers = get(runSchedule, 'wranglers', []);
      const renderWranglers = runScheduleWranglers.map((w) => `${w.firstName} ${w.lastName}`);
      const runScheduleType = get(runSchedule, 'type', '');
      const runScheduleTypes = [...runTypes];
      if (equityDeployMarkets && equityDeployMarkets.split(', ').includes(selectedMarket.name)) {
        runScheduleTypes.push({
          label: 'Equity Deployment', value: DataDriveRunTypes.EQUITY_DEPLOY,
        });
      }
      const renderTypes = runScheduleTypes
        .filter((type) => type.value === runScheduleType)
        .map((t) => t.label);
      const runScheduleTime = get(runSchedule, 'scheduledTime', '');
      const runScheduleDays = get(runSchedule, 'days', '');
      const runScheduleDuration = get(runSchedule, 'duration', 1);
      const doesRepeat = get(runSchedule, 'repeat', false);
      const renderTimes = {
        time: runScheduleTime,
        days: runScheduleDays,
        duration: runScheduleDuration,
        doesRepeat,
      };
      const vanAssignees = get(runSchedule, 'vanAssignees', {});
      const assignedOAs = this.getAssignedOAs(vanAssignees);
      let renderAssignees = [];
      if (!isEmpty(renderWranglers)) {
        renderAssignees = renderWranglers;
      } else if (!isEmpty(assignedOAs)) {
        renderAssignees = assignedOAs;
      }
      const tableData = [
        get(runSchedule, '_id', ''),
        get(runSchedule, 'name', ''),
        renderTimes,
        renderAssignees,
        get(runSchedule, 'routes', []),
        renderTypes,
        get(runSchedule, 'vanCount', 0),
        get(runSchedule, 'status', ''),
        runSchedule,
      ];
      return tableData;
    });

    return data;
  }

  renderAssignedOAs(assignees, assignee, i) {
    if (Array.isArray(assignee)) {
      return (
        <Typography
          key={assignee}
          noWrap
        >
          {`Van ${i + 1}: `}
          {assignee.join(', ')}
        </Typography>
      );
    } else {
      return (
        <Typography
          key={assignee}
          noWrap
        >
          {assignee}
          {assignees.length && i < assignees.length - 1 ? ',' : ''}
        </Typography>
      );
    }
  }

  getAssignedOAs(vanAssignees) {
    const { wranglers } = this.props;
    const assignedOAs = [];
    if (!isEmpty(vanAssignees)) {
      for (const van in vanAssignees) {
        const assignedWranglers = wranglers.filter(
          (wrangler) => vanAssignees[van].includes(wrangler._id),
        );
        const renderedAssignees = assignedWranglers.map((w) => `${w.firstName} ${w.lastName}`);
        assignedOAs.push(renderedAssignees);
      }
    }
    return assignedOAs;
  }

  getVehiclesAndLocations(runSchedule) {
    let vehiclesAndLocations = [];
    const zones = this.getZonesForRunSchedule(runSchedule);
    for (const zone of zones) {
      if (zone.isPickup) {
        const vehicles = this.getVehiclesForZone(zone);
        vehiclesAndLocations = concat(vehiclesAndLocations, vehicles);
      } else {
        const locations = this.getLocationsForZone(zone);
        vehiclesAndLocations = concat(vehiclesAndLocations, locations);
      }
    }
    return vehiclesAndLocations;
  }

  getZonesForRunSchedule(runSchedule) {
    const { allZones = [] } = this.props;
    const zones = [];
    if (runSchedule) {
      for (const routeId of runSchedule.routeIds) {
        for (const zone of allZones) {
          if (zone._id === routeId) {
            zones.push(zone);
          }
        }
      }
    }
    return zones;
  }

  getVehiclesForZone(zone) {
    const { marketVehicles = [] } = this.props;
    const vehicles = [];
    if (zone) {
      for (const vehicleId of zone.vehicleIds) {
        for (const veh of marketVehicles) {
          if (veh.humanId === vehicleId) {
            vehicles.push(veh);
            break;
          }
        }
      }
    }
    return vehicles;
  }

  getLocationsForZone(zone) {
    const { marketLocations = [] } = this.props;
    const locations = [];
    if (zone) {
      for (const location of zone.locations) {
        for (const loc of marketLocations) {
          if (loc._id === location) {
            locations.push(loc);
            break;
          }
        }
      }
    }
    return locations;
  }

  handleAddRunSchedule() {
    const { marketId } = get(this, 'props.match.params');
    const { featureFlags = [] } = this.props;
    const flags = featureFlags.filter((flag) => flag.type === FeatureFlagTypes.DATA_DRIVE);
    const newRunSchedule = {
      _id: nanoid(24),
      status: RunScheduleStatusNames.ACTIVE,
      marketId,
      routeIds: [],
      repeat: false,
      includeAllVehicles: false,
      duration: 2.5,
      days: [],
      vanCapacity: 50,
    };

    this.setState({
      isRunScheduleModalOpen: true,
      editRunSchedule: newRunSchedule,
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: !isEmpty(flags),
    });
  }

  handleEditRunSchedule(runSchedule) {
    this.setState({
      isRunScheduleModalOpen: true,
      editRunSchedule: runSchedule,
      editing: true,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleSaveRunSchedule(runSchedule, runScheduleChanges) {
    const { editing } = this.state;
    const { showSuccessSnackbar, showErrorSnackbar } = this.props;
    const { marketId } = get(this, 'props.match.params');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    if (!runScheduleChanges?.name) {
      showErrorSnackbar('Please enter a name.');
      return;
    }
    if (runScheduleChanges.repeat) {
      if (!runScheduleChanges.scheduledTime) {
        return;
      }
      if (!runScheduleChanges.days || runScheduleChanges.days.length === 0) {
        showErrorSnackbar('Please select a day for the recurring schedule.');
        return;
      }
    }
    if (!runScheduleChanges.scheduledTime) {
      showErrorSnackbar('Please select a start date.');
      return;
    }
    if (!runScheduleChanges?.isDynamic || runScheduleChanges?.isDynamic === false) {
      if (runScheduleChanges.wranglerIds && runScheduleChanges.wranglerIds.length > 2) {
        showErrorSnackbar('You can only select a maximum of 2 wranglers.');
        return;
      }
      if (!runScheduleChanges.routeIds || runScheduleChanges.routeIds.length < 1) {
        showErrorSnackbar('Please select a route.');
        return;
      }
      if (!runScheduleChanges?.deployQuantity
          || (runScheduleChanges?.deployQuantity && runScheduleChanges?.deployQuantity < 0)
      ) {
        showErrorSnackbar('Please enter a positive number of scooters to deploy.');
        return;
      }
    }
    if (runScheduleChanges?.isDynamic || runScheduleChanges?.isDynamic === true) {
      if (!runScheduleChanges?.duration || runScheduleChanges?.duration
        && (Number.isNaN(runScheduleChanges?.duration) || runScheduleChanges?.duration < 0.5)) {
        showErrorSnackbar('Please enter a positive number of hours for shift length.');
        return;
      }
      if (!runScheduleChanges?.type) {
        showErrorSnackbar('Please select a run schedule type.');
        return;
      }
      if (!runScheduleChanges?.vanCapacity
          || (runScheduleChanges?.vanCapacity && runScheduleChanges?.vanCapacity < 0)
      ) {
        showErrorSnackbar('Please enter a positive number of scooters for each van.');
        return;
      }
      if ((!runScheduleChanges.includeAllVehicles && !runScheduleChanges.vanCount)
        || (runScheduleChanges.vanCount && runScheduleChanges.vanCount < 0)
      ) {
        showErrorSnackbar('Please enter a positive number of vans for this schedule.');
        return;
      }
      if (runScheduleChanges.vanCount && runScheduleChanges.vanCount < 0) {
        showErrorSnackbar('Please enter a positive number for vans');
        return;
      }
      if (runScheduleChanges?.vanAssignees) {
        for (const key in runScheduleChanges.vanAssignees) {
          if (runScheduleChanges.vanAssignees[key].length > 2) {
            showErrorSnackbar('You can only select a maximum of 2 wranglers for each pending run.');
            return;
          }
        }
      }
      if (runScheduleChanges.wranglerIds && runScheduleChanges.wranglerIds.length > 2) {
        showErrorSnackbar('You can only select a maximum of 2 wranglers.');
        return;
      }
    }

    if (runSchedule && runScheduleChanges) {
      if (editing) {
        this.props
          .updateRunSchedule(runSchedule._id, runScheduleChanges)
          .then(() => showSuccessSnackbar('Successfully updated run'))
          .then(() => this.props.fetchRunSchedules(query))
          .then(() => this.props.fetchZones(query))
          .then(() => this.props.fetchFeatureFlags(marketId))
          .catch((error) => {
            showErrorSnackbar(`Error updating run: ${error}`);
          });
      } else {
        this.props
          .createRunSchedule(runScheduleChanges)
          .then(() => showSuccessSnackbar('Successfully created run schedule'))
          .then(() => this.props.fetchRunSchedules(query))
          .then(() => this.props.fetchZones(query))
          .then(() => this.props.fetchFeatureFlags(marketId))
          .catch((error) => {
            showErrorSnackbar(`Error creating run: ${error}`);
          });
      }
    }
    this.setState({
      isRunScheduleModalOpen: false,
      editRunSchedule: {},
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleCloseRunScheduleModal() {
    this.setState({
      isRunScheduleModalOpen: false,
      editRunSchedule: {},
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleDropdownChange(event, runSchedule) {
    const selected = event.target.getAttribute('name');
    switch (selected) {
      case EDIT_RUN_SCHEDULE:
        this.handleEditRunSchedule(runSchedule);
        break;
      case DELETE_RUN_SCHEDULE:
        this.onDeleteRunSchedule(runSchedule);
        break;
      default:
        break;
    }
  }

  render() {
    const {
      getTitleFromData,
      featureFlags = [],
      showSuccessSnackbar,
      showErrorSnackbar,
      wranglers,
      selectedMarket,
      zones,
      equityDeployMarkets,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');
    const {
      isRunScheduleModalOpen,
      editRunSchedule,
      warnBeforeDeleting,
      displayModalRadioSelector,
      editing,
    } = this.state;
    const displayData = this.data;
    const flags = featureFlags.filter((flag) => flag.type === FeatureFlagTypes.DATA_DRIVE);
    const tz = get(selectedMarket, 'tz', 'America/New_York');
    return (
      <div>
        {this.props.isLoading ? (
          <Spinner />
        ) : (
          <Paper style={styles.root} sx={{ mt: 3 }}>
            <MUIDataTable
              title={getTitleFromData('Schedules', displayData)}
              data={displayData}
              columns={this.columns}
              options={this.tableOptions}
            />
            {isRunScheduleModalOpen && editRunSchedule && !warnBeforeDeleting && (
              <RunScheduleModal
                open={isRunScheduleModalOpen}
                handleSave={this.handleSaveRunSchedule}
                handleClose={this.handleCloseRunScheduleModal}
                runSchedule={editRunSchedule}
                flags={flags}
                selectedMarket={selectedMarket}
                displayModalRadioSelector={displayModalRadioSelector}
                editing={editing}
                wranglers={wranglers}
                zones={zones}
                getVehiclesAndLocations={this.getVehiclesAndLocations}
                showErrorSnackbar={showErrorSnackbar}
                tz={tz}
                equityDeployMarkets={equityDeployMarkets}
              />
            )}
            {editRunSchedule && warnBeforeDeleting && (
              <WarnBeforeDeleting
                isOpen={warnBeforeDeleting}
                message={DELETE_WARN}
                onCancel={() => {
                  this.setState({ warnBeforeDeleting: false });
                }}
                onConfirm={() => {
                  this.props
                    .deleteRunSchedule(editRunSchedule._id)
                    .then(() => {
                      showSuccessSnackbar(DELETE_CONFIRM);
                      this.setState({ warnBeforeDeleting: false });
                      this.props.fetchRunSchedules({ market_id: marketId });
                      this.props.fetchZones({ market_id: marketId });
                      this.props.fetchFeatureFlags(marketId);
                    })
                    .catch((error) => {
                      showErrorSnackbar(`${DELETE_ERROR}: ${error}`);
                    });
                }}
              />
            )}
          </Paper>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...runScheduleActions,
  ...wranglerActions,
  ...zoneActions,
  ...vehicleActions,
  ...locationActions,
  ...featureFlagsActions,
  ...snackbarActions,
}, dispatch);

const mapStateToProps = ({
  markets, runSchedules, wranglers, zones, vehicles, locations, featureFlags, settings,
}) => ({
  isLoading: runSchedules.isLoading || zones.isLoading || vehicles.isLoading
             || locations.isLoading || featureFlags.isLoading,
  selectedMarket: markets.selectedMarket,
  runSchedules: runSchedules.runSchedules,
  wranglers: wranglers.activeMarketWranglers,
  zones: zones.noDynamicZones,
  allZones: zones.marketZones,
  marketVehicles: vehicles.marketVehicles,
  marketLocations: locations.marketLocations,
  featureFlags: featureFlags.featureFlags,
  equityDeployMarkets: settings.equityDeployMarkets,
});

export const RunScheduleTable = withPersistentSettings(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withRouter(BaseRunScheduleTable)),
  'RunScheduleTable',
);

export default { RunScheduleTable, BaseRunScheduleTable };
